import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSdk } from "@app/shared/lib/hooks";
import { useSchool } from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";

import type { CabinetPage } from "./types";

type CabinetContextType = {
  pages: CabinetPage[];
  themeCss: string;
  isLoading: boolean;
  getPageBySegment: (segment: string) => CabinetPage | undefined;
  getPageBySegments: (...segments: string[]) => CabinetPage | undefined;
  rootPages: CabinetPage[];
  childrenOf: (parentId: string) => CabinetPage[];
};

const CabinetContext = createContext<CabinetContextType>({
  pages: [],
  themeCss: "",
  isLoading: true,
  getPageBySegment: () => undefined,
  getPageBySegments: () => undefined,
  rootPages: [],
  childrenOf: () => [],
});

export const CabinetProvider: FC<PropsWithChildren> = ({ children }) => {
  const sdk = useSdk();
  const school = useSchool();
  const { isAuthorized } = useUser();
  const [pages, setPages] = useState<CabinetPage[]>([]);
  const [themeCss, setThemeCss] = useState<string>("");
  const [isLoading, setLoading] = useState(false);

  const fetchPages = useCallback(async () => {
    if (!school.isCabinetEnabled || !isAuthorized) {
      setPages([]);
      setThemeCss("");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await sdk.api.get<{ items: CabinetPage[]; themeCss?: string }>("/cabinet-page");
      if (result.success) {
        setPages(result.body.items ?? []);
        setThemeCss(result.body.themeCss ?? "");
      }
    } finally {
      setLoading(false);
    }
  }, [sdk.api, school.isCabinetEnabled, isAuthorized]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const getPageBySegment = useCallback(
    (segment: string) => pages.find((p) => p.segment === segment),
    [pages],
  );

  const getPageBySegments = useCallback(
    (...segments: string[]) => {
      let parentId: string | null = null;
      let current: CabinetPage | undefined;
      for (const seg of segments) {
        current = pages.find((p) => p.segment === seg && p.parentId === parentId);
        if (!current) return undefined;
        parentId = current.id;
      }
      return current;
    },
    [pages],
  );

  const rootPages = useMemo(
    () => pages.filter((p) => !p.parentId && p.isVisible).sort((a, b) => a.position - b.position),
    [pages],
  );

  const childrenOf = useCallback(
    (parentId: string) => {
      const parent = pages.find((p) => p.id === parentId);
      if (parent && !parent.isVisible) return [];
      return pages
        .filter((p) => p.parentId === parentId && p.isVisible)
        .sort((a, b) => a.position - b.position);
    },
    [pages],
  );

  return (
    <CabinetContext.Provider
      value={useMemo(
        () => ({ pages, themeCss, isLoading, getPageBySegment, getPageBySegments, rootPages, childrenOf }),
        [pages, themeCss, isLoading, getPageBySegment, getPageBySegments, rootPages, childrenOf],
      )}
    >
      {children}
    </CabinetContext.Provider>
  );
};

export const useCabinet = () => useContext(CabinetContext);
